import { Link } from "react-router-dom";
import {
    HeartIcon,
    LightningIcon,
    GlobeIcon,
    UsersThreeIcon,
    TargetIcon,
    SparkleIcon,
    ArrowRightIcon,
} from "@phosphor-icons/react";
import Seo from "@/components/Seo";

const VALUES = [
    {
        icon: HeartIcon,
        title: "India First",
        body: "Every product decision starts with one question — does it serve Indian creators and viewers? If not, we don't build it.",
    },
    {
        icon: LightningIcon,
        title: "Speed Matters",
        body: "From 2G to 5G, reels should load instantly. We engineer for the next billion, not the next 100 million.",
    },
    {
        icon: GlobeIcon,
        title: "Languages of India",
        body: "Hindi, Tamil, Telugu, Bangla, Marathi, Gujarati, Kannada, Punjabi, Malayalam, Odia, Assamese, Bhojpuri and more.",
    },
    {
        icon: TargetIcon,
        title: "Creator Economy",
        body: "Fair revenue share, transparent algorithm, zero gatekeeping. If you create, you should earn.",
    },
];

const TIMELINE = [
    { year: "2024", title: "Idea born", body: "Three friends in Bengaluru sketched the concept on a chai-tapri napkin." },
    { year: "2025", title: "Beta launch", body: "Onboarded 5,000 creators across 8 Indian languages. Closed beta scaled to 50K MAU." },
    { year: "2026", title: "Public launch", body: "Domain india-reels.com goes live. Apps land on Play Store & App Store. India ready to scroll." },
    { year: "Next", title: "Creator Fund", body: "Direct UPI payouts to top creators. Brand-collab marketplace. The desi creator economy goes mainstream." },
];

export default function About() {
    return (
        <div className="px-5 md:px-10 max-w-5xl mx-auto py-16 md:py-24" data-testid="about-page">
            <Seo title="About India Reels" description="India Reels is a 100% Indian short-video platform, independently owned and built for Indian creators." path="/about" />
            <div className="text-xs uppercase tracking-[0.3em] text-secondary font-bold mb-3">
                about us
            </div>
            <h1 className="font-heading font-black text-4xl md:text-6xl leading-tight">
                We&apos;re building India&apos;s
                <br />
                <span className="gradient-text">own reels platform.</span>
            </h1>
            <p className="mt-6 text-lg text-white/70 max-w-3xl leading-relaxed">
                India Reels is a 100% Indian short-video platform — independently owned, hosted in
                India, and built ground-up for Indian creators, languages, and tastes. We believe
                India deserves a stage that speaks its language and shares its values.
            </p>

            <div className="mt-14 grid md:grid-cols-2 gap-6">
                <div className="rounded-3xl border border-white/10 bg-surface p-8">
                    <div className="w-12 h-12 rounded-2xl bg-primary/15 flex items-center justify-center mb-4">
                        <SparkleIcon size={26} weight="fill" color="#E01A4F" />
                    </div>
                    <h2 className="font-heading font-bold text-2xl mb-3">Our Mission</h2>
                    <p className="text-text-secondary leading-relaxed">
                        Empower every Indian — from Anantnag to Kanyakumari — to share their story
                        in their own language, on their own terms, and earn fairly from it.
                    </p>
                </div>
                <div className="rounded-3xl border border-white/10 bg-surface p-8">
                    <div className="w-12 h-12 rounded-2xl bg-secondary/15 flex items-center justify-center mb-4">
                        <UsersThreeIcon size={26} weight="fill" color="#FFA700" />
                    </div>
                    <h2 className="font-heading font-bold text-2xl mb-3">Our Vision</h2>
                    <p className="text-text-secondary leading-relaxed">
                        To be the default short-video destination for India — where 500 million
                        Indians create, watch and connect, without depending on foreign platforms.
                    </p>
                </div>
            </div>

            <div className="mt-20">
                <div className="text-xs uppercase tracking-[0.3em] text-secondary font-bold mb-3">
                    what we stand for
                </div>
                <h2 className="font-heading font-black text-3xl md:text-4xl mb-10">Our values</h2>
                <div className="grid md:grid-cols-2 gap-5">
                    {VALUES.map((v) => (
                        <div
                            key={v.title}
                            data-testid={`value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
                            className="rounded-3xl border border-white/10 bg-surface p-7 hover:border-white/20 transition-colors"
                        >
                            <div className="w-11 h-11 rounded-2xl bg-white/5 flex items-center justify-center mb-4">
                                <v.icon size={24} weight="duotone" color="#FFA700" />
                            </div>
                            <h3 className="font-heading font-bold text-xl mb-2">{v.title}</h3>
                            <p className="text-text-secondary text-sm leading-relaxed">{v.body}</p>
                        </div>
                    ))}
                </div>
            </div>

            <div className="mt-20">
                <div className="text-xs uppercase tracking-[0.3em] text-secondary font-bold mb-3">
                    our journey
                </div>
                <h2 className="font-heading font-black text-3xl md:text-4xl mb-10">
                    From napkin to nation
                </h2>
                <div className="space-y-5">
                    {TIMELINE.map((t, i) => (
                        <div
                            key={t.year}
                            data-testid={`timeline-${i}`}
                            className="flex flex-col sm:flex-row gap-4 sm:gap-6 rounded-3xl border border-white/10 bg-surface/60 p-6"
                        >
                            <div className="sm:w-32 shrink-0">
                                <div className="font-heading font-black text-2xl gradient-text">
                                    {t.year}
                                </div>
                            </div>
                            <div className="flex-1">
                                <h3 className="font-heading font-bold text-lg mb-1">{t.title}</h3>
                                <p className="text-text-secondary text-sm leading-relaxed">{t.body}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="mt-20 rounded-3xl bg-gradient-to-br from-primary/20 via-surface to-secondary/10 border border-white/10 p-10 md:p-14 text-center">
                <h2 className="font-heading font-black text-3xl md:text-4xl">
                    Join the <span className="gradient-text">desi creator movement</span>.
                </h2>
                <p className="text-white/70 mt-4 max-w-xl mx-auto">
                    Download the app and start scrolling, or get in touch if you want to partner
                    with us.
                </p>
                <div className="mt-8 flex flex-wrap gap-3 justify-center">
                    <Link
                        to="/#download"
                        data-testid="about-download-btn"
                        className="btn-primary px-6 py-3 inline-flex items-center gap-2"
                    >
                        Download App
                        <ArrowRightIcon size={16} weight="bold" />
                    </Link>
                    <Link
                        to="/contact"
                        data-testid="about-contact-btn"
                        className="btn-ghost px-6 py-3"
                    >
                        Contact us
                    </Link>
                </div>
            </div>
        </div>
    );
}
